'use client';

import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { styled } from '@linaria/react';
import Image from 'next/image';
import useDrawerStore from '@fe-news/context/drawer-zustand';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { useState, useEffect } from 'react';
import { createCookie, getCookie } from '@fe-common-utils/libs/cookie';

const ButtonContainer = styled.div`
  position: relative;
  font-size: 14px;
  font-weight: normal;
  line-height: 1;
  text-align: center;
  color: #848d97;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const Icon = styled.button`
  cursor: pointer;
  margin-bottom: 8px;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    margin-bottom: 0px;
  }

  img {
    width: 36px;
    height: 36px;

    @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
      width: 24px;
      height: 24px;
    }
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 0px;

  @keyframes handshake {
    0% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-3deg);
    }
    50% {
      transform: rotate(0deg);
    }
    75% {
      transform: rotate(3deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }

  animation: handshake 1s infinite;
  animation-timing-function: linear;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    top: 35px;
    right: 50%;
  }

  @media screen and (min-width: ${SCREEN_SIZE.DESKTOP}px) {
    left: 39px;
  }
`;

const CloseButton = styled.button`
  cursor: pointer;
  position: absolute;
  width: 35px;
  height: 35px;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    top: 45px;
    right: 50%;
  }

  @media screen and (min-width: ${SCREEN_SIZE.DESKTOP}px) {
    top: 0;
    left: 168px;
  }
`;

const TOOLTIP_LAST_SHOWN_DATE_KEY = 'tooltipLastShownDate';

const DISPLAY_DURATION = 5000;

const formatCount = (value: number) => {
  if (value / 1000 > 1) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return value.toString();
};

type CommentButtonProps = {
  commentCount?: number;
};

const CommentButton: React.FC<CommentButtonProps> = ({ commentCount }) => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`, true);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { setIsOpenDrawer } = useDrawerStore();

  const onClickChat = () => {
    setIsOpenDrawer(true);
  };

  const onClickCloseTooltip = () => {
    setIsTooltipVisible(false);
  };

  useEffect(() => {
    const alreadyShown = getCookie(TOOLTIP_LAST_SHOWN_DATE_KEY, 0);
    let timer: NodeJS.Timeout;
    let displayTimer: NodeJS.Timeout;

    if (!alreadyShown) {
      setIsTooltipVisible(true);

      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);

      timer = setTimeout(() => {
        createCookie({
          key: TOOLTIP_LAST_SHOWN_DATE_KEY,
          value: 1,
          options: { path: '/', expires: endOfDay }
        });
      }, 5000);

      displayTimer = setTimeout(() => {
        setIsTooltipVisible(false);
      }, DISPLAY_DURATION);
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(displayTimer);
    };
  }, []);

  return (
    <ButtonContainer>
      <Icon onClick={onClickChat} aria-label='Open chat'>
        <Image
          src={isDesktop ? '/assets/icons/topBar/icon-chat.svg' : '/assets/icons/topBar/icon-comment.svg'}
          alt='chat-icon'
          width={isDesktop ? 36 : 24}
          height={isDesktop ? 36 : 24}
        />
      </Icon>
      {commentCount && <p>{formatCount(commentCount)}</p>}
      {isTooltipVisible && (
        <Tooltip>
          <Image
            src={
              isDesktop
                ? '/assets/icons/topBar/icon-desktop-tooltip.png'
                : '/assets/icons/topBar/icon-mobile-tooltip.png'
            }
            alt='Chat tooltip'
            width={isDesktop ? 165 : 163}
            height={isDesktop ? 38 : 45}
          />
        </Tooltip>
      )}
      <CloseButton onClick={onClickCloseTooltip} aria-label='Close tooltip' />
    </ButtonContainer>
  );
};

export default CommentButton;
